<template>
  <!--
    A form where cameraManagementConfigurations are created.
  -->
  <div class="cameraManagementConfigForm">
    <LoadingPlaceholder v-if="loading" />
    <template v-else>
      <template v-if="readyToDisplay">
        <div class="form-group">
          <label>{{ $t('camera') }}</label>
          <select
            v-model="selectedCamera"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].IpAddress`) }]"
          >
            <option
              v-for="(camera, index) in networkCameras"
              :key="`networkCam${ index }`"
              :value="camera"
            >
              {{ camera.manufacturer }} - {{ camera.ipAddress }}
            </option>
          </select>
          <span
            v-show="errors.has(`CameraConfigs[0].IpAddress`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].IpAddress`) }}</span>
        </div>
        <div class="form-group">
          <label>{{ $t('ipAddress') }}</label>
          <input
            v-model="cameraConfig.ipAddress"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].IpAddress`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].IpAddress`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].IpAddress`) }}</span>
        </div>
        <div class="form-group">
          <label>{{ $t('manufacturer') }}</label>
          <input
            v-model="cameraConfig.manufacturer"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].Manufacturer`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].Manufacturer`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].Manufacturer`) }}</span>
        </div>
        <div class="form-group">
          <label>{{ $t('model') }}</label>
          <input
            v-model="cameraConfig.model"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].Model`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].Model`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].Model`) }}</span>
        </div>
        <div class="form-group">
          <label>{{ $t('username') }}</label>
          <input
            v-model="cameraConfig.username"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].Username`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].Username`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].Username`) }}</span>
        </div>
        <div class="form-group">
          <label>{{ $t('password') }}</label>
          <input
            v-model="cameraConfig.password"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].Password`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].Password`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].Password`) }}</span>
        </div>
        <div class="form-group">
          <label>Pan / tilt {{ $t('ipAddress').toLowerCase() }}</label>
          <input
            v-model="cameraConfig.panTiltConfig.ipAddress"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[0].PanTiltConfig.IpAddress`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[0].PanTiltConfig.IpAddress`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[0].PanTiltConfig.IpAddress`) }}</span>
        </div>
        <div class="form-group">
          <label>CameraOperator Host IP</label>
          <input
            v-model="cameraConfig.cameraOperatorHostIp"
            :class="['form-control', { 'is-invalid': errors.has(`CameraConfigs[${ usedId }].CameraOperatorHostIp`) }]"
            type="text"
          >
          <span
            v-show="errors.has(`CameraConfigs[${ usedId }].CameraOperatorHostIp`)"
            class="badge badge-danger"
          >{{ errors.first(`CameraConfigs[${ usedId }].CameraOperatorHostIp`) }}</span>
        </div>
        <div class="form-group">
          <label
            for="ptHeadType"
            class="form-label"
          >PT Head</label>
          <Multiselect
            id="ptHeadType"
            v-model="cameraConfig.ptHeadType"
            placeholder="Select type"
            :options="['Auto Discover', 'Alturos', 'Eneo']"
            select-label=""
            :close-on-select="true"
            class="mb-4"
          />
        </div>
        <div class="form-group row mb-2">
          <label class="col-12 col-sm-3 col-form-label">{{ $t('active') }}</label> 
          <div class="col-12 col-sm-9">
            <toggle-button
              v-model="cameraConfig.active"
              :labels="{ checked: $t('true'), unchecked: $t('false') }"
              :class="['m-0 ml-4 mt-2', { 'is-invalid': this.$validator.errors.has('Active') }]"
              :width="70"
              :height="30"
              :font-size="12"
            />
          </div>
        </div>
        <span
          v-show="errors.has('Active')"
          class="badge badge-danger"
        >{{ errors.first('Active') }}</span>
        <button
          class="btn btn-primary float-right"
          @click="addConfig"
        >
          <font-awesome-icon
            class="mr-2"
            icon="plus"
          />
          <span>{{ $t('add') }}</span>
        </button>
        <div class="clearfix" />
      </template>
      <template v-else>
        {{ $t('noDataAvailable') }}
      </template>
    </template>
  </div>
</template>

<script>
import { errorMixin } from '@/mixins/errorMixin.js';

export default {
  name: 'CameraManagementConfigForm',
  components: {
    Multiselect: () => import('vue-multiselect'),
  },
  mixins: [
    errorMixin
  ],
  props: {
    installationId: {
      type: String,
      required: true
    },
    laneNumber: {
      type: String,
      required: false,
      default () {
        return null;
      }
    }
  },
  data () {
    return {
      configData: null,
      selectedCamera: null,
      networkCameras: null,
      loading: true,
    }
  },
  computed: {
    cameraConfig () {
      return this.configData.cameraConfigs[0];
    },
    readyToDisplay () {
      if (this.configData == null) {
        return false;
      }
      if (this.configData.cameraConfigs == null) {
        return false;
      }
      return true;
    }
  },
  watch: {
    selectedCamera () {
      if (this.selectedCamera == null)
      {
        return;
      }
      else
      {
        this.cameraConfig.ipAddress = this.selectedCamera.ipAddress;
        this.cameraConfig.manufacturer = this.selectedCamera.manufacturer;
        this.cameraConfig.model = this.selectedCamera.model;
      }
    }
  },
  async created () {
    await this.getAvailableCameras();
    this.getDefaultConfig();
  },
  methods: {
    addConfig () {
      let url = `/Config/AddCameraManagementConfig?installationId=${ this.installationId }`; 
      if (this.laneNumber)
      {
        url += `&laneNumber=${ this.laneNumber }`;
      }
      this.axios.post(url, this.cameraConfig)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.status == null) {
            return;
          }
          if (response.status == 204) {
            this.$emit('hide');
            this.$emit('setReloadButton', true);
            this.$snotify.error(this.$t('configReload.failedReload'));
          } else {
            this.$emit('setReloadButton', false);
            if (response.status != 200) {
              return;
            }
          }
          
          this.$snotify.success(this.$t('cameraManagementConfigFormComponent.successfullySaved'));
          this.error_clear();
          this.$emit('getConfig');
        })
        .catch((error) => {
          this.error_clear();
          this.error_validate(error);
        });
    },
    async getAvailableCameras () {
     let url = `/DeviceManagement/GetDevices?installationId=${ this.installationId }`; 
     await this.axios.get(url)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          if (response.status == null) {
            return;
          }
          if (response.status != 200) {
            return
          }
          this.networkCameras = response.data.filter(p => p.discoveredDeviceType == "camera");
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDefaultConfig () {
      let url = `/Config/GetDefaultCameraManagementConfig?installationId=${ this.installationId }`; 
      if (this.laneNumber)
      {
        url += `&laneNumber=${ this.laneNumber }`;
      }
      this.axios.get(url)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          if (response.data.cameraConfigs == null) {
            return;
          }
          this.configData = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
      }
  }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>